import React, { useState, useEffect } from 'react';
import { CalendarPlus, Clock, ChevronRight, AlertTriangle, Syringe, Stethoscope, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getBookingOperations, getAvailableSlots, getAppointmentSettings, createBookingRequest, getMyAppointments, requestCancellation } from '../../services/api';
import { toast } from 'sonner';
import PetAvatar from '../PetAvatar';

const STATUS_MAP = {
  pending: { label: 'Onay Bekliyor', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-600' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-600' },
  cancel_requested: { label: 'İptal Talebi', color: 'bg-orange-100 text-orange-600' },
  completed: { label: 'Tamamlandı', color: 'bg-blue-100 text-blue-600' },
};

const TYPE_CONFIG = {
  vaccine: { label: 'Aşılar', icon: Syringe, color: 'text-red-600', bg: 'bg-red-50' },
  treatment: { label: 'Tedaviler', icon: Stethoscope, color: 'text-blue-600', bg: 'bg-blue-50' },
  service: { label: 'Hizmetler', icon: Wrench, color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const CustomerAppointments = ({ pets }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModal, setBookingModal] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [operations, setOperations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clinicSettings, setClinicSettings] = useState(null);

  useEffect(() => { loadAppointments(); }, []);

  const loadAppointments = async () => {
    try { setAppointments((await getMyAppointments()).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openBooking = async () => {
    setStep(1);
    setSelectedPet(null);
    setSelectedOperation(null);
    setSelectedDate('');
    setSlots([]);
    try { setOperations((await getBookingOperations()).data); } catch (e) { toast.error('İşlemler yüklenemedi'); }
    try { setClinicSettings((await getAppointmentSettings()).data); } catch (e) { console.error(e); }
    setBookingModal(true);
  };

  const selectPet = (pet) => { setSelectedPet(pet); setStep(2); };

  const selectOperation = (op) => { setSelectedOperation(op); setStep(3); };

  const selectDate = async (date) => {
    setSelectedDate(date);
    setSlotsLoading(true);
    try {
      const res = await getAvailableSlots(date, selectedOperation?.id);
      setSlots(res.data.slots || []);
      if (res.data.is_closed) toast.info('Bu gün klinik kapalı');
    } catch (e) { toast.error('Slotlar yüklenemedi'); }
    finally { setSlotsLoading(false); }
    setStep(4);
  };

  const selectSlot = async (slot) => {
    setSubmitting(true);
    try {
      await createBookingRequest({
        pet_id: selectedPet.id, operation_id: selectedOperation.id,
        date: selectedDate, start_time: slot.time,
      });
      toast.success('Randevu talebiniz oluşturuldu! Klinik onayını bekleyin.');
      setBookingModal(false);
      loadAppointments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Randevu oluşturulamadı');
    } finally { setSubmitting(false); }
  };

  const handleCancelRequest = async (bookingId) => {
    try {
      await requestCancellation(bookingId);
      toast.success('İptal talebiniz kliniğe iletildi');
      loadAppointments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İptal talebi oluşturulamadı');
    }
  };

  // Group operations by type
  const groupedOps = operations.reduce((acc, op) => {
    const type = op.type || 'service';
    if (!acc[type]) acc[type] = [];
    acc[type].push(op);
    return acc;
  }, {});

  // Date options with closed day info
  const getDateOptions = () => {
    const closedDays = new Set();
    if (clinicSettings?.working_hours) {
      clinicSettings.working_hours.forEach(wh => {
        if (wh.is_closed) closedDays.add(wh.day);
      });
    }
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const dow = (d.getDay() + 6) % 7;
      dates.push({
        value: d.toISOString().split('T')[0],
        label: d.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' }),
        isToday: i === 0,
        isClosed: closedDays.has(dow),
      });
    }
    return dates;
  };

  const activeAppointments = appointments.filter(a => ['pending', 'confirmed', 'cancel_requested'].includes(a.status));
  const pastAppointments = appointments.filter(a => ['cancelled', 'rejected', 'completed'].includes(a.status));

  if (loading) return <div className="ios-card text-center py-8"><p className="text-[#8E8E93]">Yükleniyor...</p></div>;

  const stepTitles = { 1: 'Evcil Hayvan Seçin', 2: 'İşlem Seçin', 3: 'Tarih Seçin', 4: 'Saat Seçin' };

  return (
    <div className="space-y-4 animate-fade-in">
      <Button onClick={openBooking} className="w-full bg-[#6b6de3] hover:bg-[#5658c4] rounded-full h-12" data-testid="new-booking-btn">
        <CalendarPlus size={18} className="mr-2" /> Yeni Randevu Al
      </Button>

      {/* Active Appointments */}
      {activeAppointments.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-2">Aktif Randevular</h3>
          <div className="space-y-2">
            {activeAppointments.map(apt => {
              const status = STATUS_MAP[apt.status] || { label: apt.status, color: 'bg-gray-100 text-gray-600' };
              return (
                <div key={apt.id} className="ios-card" data-testid={`appointment-${apt.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <PetAvatar species={apt.pet_species} size={20} />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{apt.pet_name}</p>
                        <p className="text-xs text-[#8E8E93]">{apt.operation_name}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${status.color}`}>{status.label}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-xs text-[#8E8E93]">
                      <Clock size={12} />
                      {new Date(apt.date + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {' '}{apt.start_time} - {apt.end_time}
                    </div>
                    {apt.duration_minutes > 30 && (
                      <span className="text-[10px] bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{apt.duration_minutes} dk</span>
                    )}
                  </div>
                  {(apt.status === 'confirmed' || apt.status === 'pending') && (
                    <button onClick={() => handleCancelRequest(apt.id)}
                      className="mt-2 text-xs text-red-500 hover:text-red-600 font-medium"
                      data-testid={`cancel-btn-${apt.id}`}>
                      İptal Talebi Oluştur
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-2">Geçmiş</h3>
          <div className="space-y-2">
            {pastAppointments.map(apt => {
              const status = STATUS_MAP[apt.status] || { label: apt.status, color: 'bg-gray-100 text-gray-600' };
              return (
                <div key={apt.id} className="ios-card opacity-70" data-testid={`past-apt-${apt.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{apt.pet_name} - {apt.operation_name}</p>
                      <p className="text-xs text-[#8E8E93]">{new Date(apt.date + 'T00:00:00').toLocaleDateString('tr-TR')} {apt.start_time}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${status.color}`}>{status.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {appointments.length === 0 && (
        <div className="ios-card text-center py-8">
          <CalendarPlus size={48} className="text-[#C7C7CC] mx-auto mb-3" />
          <p className="text-[#8E8E93]">Henüz randevunuz yok</p>
          <p className="text-xs text-[#8E8E93] mt-1">Yukarıdaki butonu kullanarak randevu alabilirsiniz</p>
        </div>
      )}

      {/* Booking Flow Modal */}
      <Dialog open={bookingModal} onOpenChange={setBookingModal}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-0">
          {/* Fixed header */}
          <div className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogHeader>
              <DialogTitle className="text-base">{stepTitles[step]}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-2 mt-3">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-[#6b6de3]' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>

          {/* Scrollable content */}
          <div className="px-5 pb-5 pt-3 overflow-y-auto max-h-[calc(85vh-90px)]">
            {/* Step 1: Select Pet */}
            {step === 1 && (
              <div className="space-y-2">
                {pets.map(pet => (
                  <button key={pet.id} onClick={() => selectPet(pet)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                    data-testid={`select-pet-${pet.id}`}>
                    <PetAvatar species={pet.species} gender={pet.gender} size={24} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{pet.name}</p>
                      <p className="text-xs text-[#8E8E93]">{pet.species === 'dog' ? 'Köpek' : pet.species === 'cat' ? 'Kedi' : pet.species}</p>
                    </div>
                    <ChevronRight size={16} className="text-[#8E8E93]" />
                  </button>
                ))}
                {pets.length === 0 && <p className="text-center text-[#8E8E93] py-4 text-sm">Kayıtlı evcil hayvanınız yok</p>}
              </div>
            )}

            {/* Step 2: Select Operation (grouped) */}
            {step === 2 && (
              <div className="space-y-4">
                <button onClick={() => setStep(1)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                  <ChevronRight size={12} className="rotate-180" /> Geri
                </button>
                {['vaccine', 'treatment', 'service'].map(type => {
                  const ops = groupedOps[type];
                  if (!ops || ops.length === 0) return null;
                  const cfg = TYPE_CONFIG[type];
                  const Icon = cfg.icon;
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-6 h-6 ${cfg.bg} rounded-md flex items-center justify-center`}>
                          <Icon size={13} className={cfg.color} />
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
                        <span className="text-[10px] text-[#8E8E93]">({ops.length})</span>
                      </div>
                      <div className="space-y-1.5">
                        {ops.map(op => (
                          <button key={op.id} onClick={() => selectOperation(op)}
                            className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                            data-testid={`select-op-${op.id}`}>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{op.name}</p>
                              {op.duration_minutes && <span className="text-[10px] text-[#8E8E93]">{op.duration_minutes} dk</span>}
                            </div>
                            <ChevronRight size={16} className="text-[#8E8E93]" />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {operations.length === 0 && <p className="text-center text-[#8E8E93] py-4 text-sm">Klinik henüz işlem tanımlamamış</p>}
              </div>
            )}

            {/* Step 3: Select Date */}
            {step === 3 && (
              <div className="space-y-2">
                <button onClick={() => setStep(2)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                  <ChevronRight size={12} className="rotate-180" /> Geri
                </button>
                {selectedOperation?.duration_minutes > 30 && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-xl mb-2">
                    <AlertTriangle size={16} className="text-yellow-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-700">
                      Seçtiğiniz işlem <strong>{selectedOperation.duration_minutes} dakika</strong> sürmektedir.
                    </p>
                  </div>
                )}
                <div className="max-h-[300px] overflow-y-auto space-y-1.5">
                  {getDateOptions().map(d => (
                    d.isClosed ? (
                      <div key={d.value}
                        className="w-full flex items-center justify-between p-3 rounded-xl bg-red-50/60 opacity-60 cursor-not-allowed"
                        data-testid={`select-date-${d.value}`}>
                        <span className="text-sm text-gray-400 capitalize">{d.label}</span>
                        <span className="text-[10px] bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-medium">Kapalı</span>
                      </div>
                    ) : (
                      <button key={d.value} onClick={() => selectDate(d.value)}
                        className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F2F2F7] hover:bg-gray-200 transition-colors text-left"
                        data-testid={`select-date-${d.value}`}>
                        <span className="text-sm text-gray-900 capitalize">{d.label}</span>
                        {d.isToday && <span className="text-[10px] bg-[#6b6de3] text-white px-2 py-0.5 rounded-full">Bugün</span>}
                      </button>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Select Time Slot */}
            {step === 4 && (
              <div className="space-y-2">
                <button onClick={() => setStep(3)} className="text-xs text-[#6b6de3] mb-1 flex items-center gap-1">
                  <ChevronRight size={12} className="rotate-180" /> Geri
                </button>
                <div className="p-3 bg-[#F2F2F7] rounded-xl mb-3">
                  <p className="text-xs text-[#8E8E93]">
                    <strong>{selectedPet?.name}</strong> - {selectedOperation?.name} - {new Date(selectedDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
                  </p>
                </div>
                {slotsLoading ? (
                  <p className="text-center text-[#8E8E93] py-4 text-sm">Yükleniyor...</p>
                ) : slots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                    {slots.map(slot => (
                      <button key={slot.time} onClick={() => selectSlot(slot)} disabled={submitting}
                        className="p-3 rounded-xl bg-[#F2F2F7] hover:bg-[#6b6de3] hover:text-white transition-colors text-center group"
                        data-testid={`select-slot-${slot.time}`}>
                        <p className="text-sm font-medium">{slot.time}</p>
                        <p className="text-[10px] text-[#8E8E93] group-hover:text-white/70">{slot.end_time}</p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Clock size={36} className="text-[#C7C7CC] mx-auto mb-2" />
                    <p className="text-[#8E8E93] text-sm">Bu tarihte müsait saat yok</p>
                    <button onClick={() => setStep(3)} className="text-xs text-[#6b6de3] mt-2 font-medium">Başka tarih seçin</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerAppointments;
